export interface IBundleReference {
  /**
   * The ID of the module.
   */
  id: string;

  /**
   * If present, this is the name of a field on the module returned from the associated bundle that maps to
   * this library. Otherwise, the module returned from the bundle maps to this library.
   */
  entryName?: string;
}

export interface IStandardLibraryModule {
  /**
   * Reference (id and entryName pair) to the bundle
   */
  bundleReference: IBundleReference;

  /**
   * The ID of the bundle to preload.
   */
  preloadId: string;

  /**
   * The IDs of modules this module depends on.
   */
  dependentIds: string[];

  /**
   * References to modules that are swappable for this module (for example, modules that contain a superset of
   * this module).
   */
  aliases: IBundleReference[];
}

/**
 * This is a mapping from standard library module names to their manifest IDs.
 */
export const standardLibraryModules: { [moduleName: string]: IStandardLibraryModule } = {
  '@microsoft/sp-client-preview': {
    bundleReference: {
      id: '4d5eb168-6729-49a8-aec7-0e397f486b6e'
    },
    preloadId: '4d5eb168-6729-49a8-aec7-0e397f486b6e',
    dependentIds: [
      '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4',
      'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
      '02a01e42-69ab-403d-8a16-acd128661f8e',
      '974a7777-0990-4136-8fa6-95d80114c2e0'
    ],
    aliases: []
  },

  'office-ui-fabric-react': {
    bundleReference: {
      id: '02a01e42-69ab-403d-8a16-acd128661f8e'
    },
    preloadId: '02a01e42-69ab-403d-8a16-acd128661f8e',
    dependentIds: ['6aeaab4a-4d96-47fe-a0c2-9e063a2efda4'],
    aliases: []
  },

  '@microsoft/decorators': {
    bundleReference: {
      id: 'f97266fb-ccb7-430e-9384-4124d05295d3'
    },
    preloadId: 'f97266fb-ccb7-430e-9384-4124d05295d3',
    dependentIds: [
      'a8c07ea7-3ee8-4376-8afa-e0c5098615eb'
    ],
    aliases: []
  },

  '@microsoft/sp-lodash-subset': {
    bundleReference: {
      id: '73e1dc6c-8441-42cc-ad47-4bd3659f8a3a'
    },
    preloadId: 'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
    dependentIds: [],
    aliases: [{
      id: 'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
      entryName: 'spLodash'
    }]
  },

  '@ms/sp-application-base': {
    bundleReference: {
      id: '4df9bb86-ab0a-4aab-ab5f-48bf167048fb'
    },
    preloadId: '4df9bb86-ab0a-4aab-ab5f-48bf167048fb',
    dependentIds: [],
    aliases: []
  },

  '@ms/sp-canvas': {
    bundleReference: {
      id: '85093aa7-8c12-4683-91aa-47cd5e2654db'
    },
    preloadId: '85093aa7-8c12-4683-91aa-47cd5e2654db',
    dependentIds: [],
    aliases: []
  },

  '@microsoft/sp-client-base': {
    bundleReference: {
      id: 'af59c2b3-2da7-41fd-8b72-3939817960af'
    },
    preloadId: 'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
    dependentIds: [],
    aliases: [{
      id: 'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
      entryName: 'spClientBase'
    }]
  },

  '@microsoft/sp-http': {
    bundleReference: {
      id: 'c07208f0-ea3b-4c1a-9965-ac1b825211a6'
    },
    preloadId: 'c07208f0-ea3b-4c1a-9965-ac1b825211a6',
    dependentIds: [
      'a8c07ea7-3ee8-4376-8afa-e0c5098615eb'
    ],
    aliases: []
  },

  '@microsoft/sp-page-context': {
    bundleReference: {
      id: '1c4541f7-5c31-41aa-9fa8-fbc9dc14c0a8'
    },
    preloadId: '1c4541f7-5c31-41aa-9fa8-fbc9dc14c0a8',
    dependentIds: [
      'a8c07ea7-3ee8-4376-8afa-e0c5098615eb'
    ],
    aliases: []
  },

  '@ms/odsp-utilities-bundle': {
    bundleReference: {
      id: 'cc2cc925-b5be-41bb-880a-f0f8030c6aff'
    },
    preloadId: 'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
    dependentIds: [],
    aliases: [{
      id: 'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
      entryName: 'odspUtilitiesBundle'
    }]
  },

  '@microsoft/sp-webpart-base': {
    bundleReference: {
      id: '974a7777-0990-4136-8fa6-95d80114c2e0'
    },
    preloadId: '974a7777-0990-4136-8fa6-95d80114c2e0',
    dependentIds: [
      '02a01e42-69ab-403d-8a16-acd128661f8e',
      'a8c07ea7-3ee8-4376-8afa-e0c5098615eb'
    ],
    aliases: []
  },

  '@microsoft/sp-module-loader': {
    bundleReference: {
      id: '1c6c9123-7aac-41f3-a376-3caea41ed83f'
    },
    preloadId: undefined,
    dependentIds: [],
    aliases: []
  },

  'react': {
    bundleReference: {
      id: '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4',
      entryName: 'react'
    },
    preloadId: '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4',
    dependentIds: [],
    aliases: []
  },

  'react-dom': {
    bundleReference: {
      id: '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4',
      entryName: 'reactDom'
    },
    preloadId: '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4',
    dependentIds: [],
    aliases: []
  },

  'flux': {
    bundleReference: {
      id: '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4',
      entryName: 'flux'
    },
    preloadId: '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4',
    dependentIds: [],
    aliases: []
  },

  '@ms/i18n-utilities': {
    bundleReference: {
      id: 'c83d5509-ccd5-4c67-919f-2440f237927a'
    },
    preloadId: 'c83d5509-ccd5-4c67-919f-2440f237927a',
    dependentIds: [],
    aliases: []
  },

  '@ms/sp-filepicker': {
    bundleReference: {
      id: 'a81a5cf6-c9ad-4a0a-85aa-68c03d2d22a2'
    },
    preloadId: 'a81a5cf6-c9ad-4a0a-85aa-68c03d2d22a2',
    dependentIds: [
      'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
      '974a7777-0990-4136-8fa6-95d80114c2e0',
      '02a01e42-69ab-403d-8a16-acd128661f8e',
      '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4'
    ],
    aliases: []
  },

  '@ms/sp-component-utilities': {
    bundleReference: {
      id: '8494e7d7-6b99-47b2-a741-59873e42f16f'
    },
    preloadId: '8494e7d7-6b99-47b2-a741-59873e42f16f',
    dependentIds: [
      'a8c07ea7-3ee8-4376-8afa-e0c5098615eb',
      '02a01e42-69ab-403d-8a16-acd128661f8e',
      '6aeaab4a-4d96-47fe-a0c2-9e063a2efda4'
    ],
    aliases: []
  }
};



// WEBPACK FOOTER //
// /src/standardLibraryModules.ts